/** ***********
*
* <map-scope-management.service.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  pranshushrivastav [pranshu.shrivastav@soprasteria.com]
*
* All rights reserved.
**************/

import { Category, Categories } from './../models/masterdata';
import { AbstractType, Injectable, Injector } from '@angular/core';
import { get } from 'lodash';
import { Asset } from '../models/asset';
import { Event } from '../models/event';
import { PICTO_SOURCES, SCOPE_SETTINGS, PICTO, COLOR_SOURCES, PICTO_COLOR, ScopeSettings, PictureService } from '../models/scope-management.model';
import { Zone, ZoneStatus } from 'src/app/models/zone';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SignalSourceProviderValues } from '../models/signal-source';

@Injectable()
export class MapScopeManagementService {
    constructor(
        private _injector: Injector,
    ) { }

    /**
     * Return the corresponding asset pictogram regarding business rules
     * @returns SVG string pictogram
     */
    public getPictoByAsset(asset: Asset): string {
        const isQuuppa = asset.tracker?.provider === SignalSourceProviderValues.quuppa;
        const arrayPath = SCOPE_SETTINGS[asset.family.scope].pictoSourcePath;
        const source = isQuuppa ? 'quuppa' : (get(asset, arrayPath, 'default') as string);
        return this._getPictoBySource(source);
    }

    /**
     * Return the pictogram color based on the business rules
     * @param assetOrEvent Asset or Tracker Event
     * @param scope Scope of the asset family
     * @returns RGB color as an array of 3 numbers
     */
    public getPictoColor(assetOrEvent: Asset | Event, isMovementStatusDisabled=false, scope?: string): number[] {
        let source: string;
        let arrayPath: string[];
        if (this._isEvent(assetOrEvent)) { // handle event picto color for history display
            const event = assetOrEvent as Event;
            source = get(event, SCOPE_SETTINGS[scope]?.eventColorSourcePath, 'default');
        } else {
            const asset = assetOrEvent as Asset;
            // handle the case when there are loaded elements
            arrayPath = SCOPE_SETTINGS[asset.family.scope]?.colorSourcePath;
            if (this._isAssetLoadable(asset) && !this._isAssetLoaded(asset)) {
                source = 'background';
            } else {
                source = get(asset, arrayPath, 'default');
            }
        }
        return this._getPictoColorBySource(source, isMovementStatusDisabled);
    }


    /**
     * Public method which return the background pictogram
     * @returns SVG string pictogram
     */
    public getBackGroundPicto(): string {
        return this._getPictoBySource('background');
    }

    /**
     * Return the color of the background pictogram based on the business rules
     * @param assetOrEvent Asset or Tracker Event
     * @returns RGB color as an array of 3 numbers
     */
    public getBackGroundColor(assetOrEvent: Asset | Event, isMovementStatusDisabled=false): number[] {
        let source = 'background'; // default source
        if (!this._isEvent(assetOrEvent)) {
            const asset = assetOrEvent as Asset;
            const arrayPath = SCOPE_SETTINGS[asset.family.scope].colorSourcePath;
            if (this._isAssetLoadable(asset) && !this._isAssetLoaded(asset)) {
                source = get(asset, arrayPath, 'default');
            }
        }
        return this._getPictoColorBySource(source,isMovementStatusDisabled);
    }

    /**
     * Define whether the pictogram should be outlined based on the business rules
     * - if the asset is loaded, the picto should not be outlined
     * - if the asset is empty, the picto should be outlined
     * @param assetOrEvent Asset or tracker Event
     * @returns a boolean informing whether the pictogram should be outlined
     */
    public isPictoOutlined(assetOrEvent: Asset | Event): boolean { // if empty
        if (this._isEvent(assetOrEvent)) {
            return false;
        } else {
            const asset = assetOrEvent as Asset;
            return (this._isAssetLoadable(asset) && !this._isAssetLoaded(asset)) ? true : false;
        }
    }

    /**
     * Return the outline color regarding the outlined state of the pictogram
     * - The outline take the color of the picto if this one is outlined
     * - Else, no outline color
     * @returns RGB color as an array of 3 numbers
     */
    public getOutlineColor(asset: Asset, isMovementStatusDisabled=false): number[] | null {
        if (this.isPictoOutlined(asset)) {
            const arrayPath = SCOPE_SETTINGS[asset.family?.scope]?.colorSourcePath;
            const source = get(asset, arrayPath, 'default');
            return this._getPictoColorBySource(source,isMovementStatusDisabled);
        } else {
            return null;
        }
    }

    /**
    * get zone color depending on zone status
    * @zoneAttr zone attribute from esri API
    */
    public getZoneColor(zoneAttr: Zone): { background: string, border: string } {
        switch (zoneAttr?.zoneStatus) {
            case ZoneStatus.booked:
                return { background: `rgba(${PICTO_COLOR.orange},0.25)`, border: `rgba(${PICTO_COLOR.orange},1)` };
            case ZoneStatus.occupied:
                return { background: `rgba(${PICTO_COLOR.red},0.25)`, border: `rgba(${PICTO_COLOR.red},1)` };
            case ZoneStatus.available:
                return { background: `rgba(${PICTO_COLOR.green.join(',')},0.25)`, border: `rgba(${PICTO_COLOR.green.join(',')},1)` };
            default:
                return { background: 'rgba(0, 0, 0, 0.25)', border: 'rgba(0, 0, 0,1)' };
        }
    }

    /**
     * Return a SVG pictogram based on the source name
     * @param source the pictogram source name. Can be an asset status, an event state or a background picto
     * @returns SVG string pictogram
     */
    private _getPictoBySource(source: string): string {
        const picto = PICTO_SOURCES.find(pictoSource => pictoSource.source.toLowerCase() === source?.toLowerCase())?.picto;
        return picto || PICTO.markerSVG;
    }

    /**
     * Return a color regarding the source name
     * @param source source name mapped with the color from the COLOR_SOURCES constant
     * @returns RGB color as an array of 3 numbers
     */
    private _getPictoColorBySource(source: string, isMovementStatusDisabled = false, isopactity?: boolean): number[] {
        const key = source?.toLowerCase();
        const overrideWhenDisabled = ['start', 'otm', 'stop'];
        const matchedSource = COLOR_SOURCES.find(item => item.source === key);
        if (isMovementStatusDisabled && overrideWhenDisabled.includes(key)) {
            return PICTO_COLOR.black;
        }
        return matchedSource?.color || PICTO_COLOR.default;
    }

    /**
     * Define if the asset is loadable. It impact the further aspect of the pictogram
     */
    private _isAssetLoadable(asset: Asset): boolean {
        return SCOPE_SETTINGS[asset.family.scope].isLoadable;
    }

    /**
     * Identify whether the asset is loaded
     */
    private _isAssetLoaded(asset: Asset): boolean {
        return (asset.loadedElements && asset.loadedElements.length > 0) ? true : false;
    }

    /**
     * Identify if the parameter is an asset or an event, based on the insert_time property
     * which is available only for events
     * @param assetOrEvent Asset or Event
     */
    private _isEvent(assetOrEvent: Asset | Event): boolean {
        return (assetOrEvent as Event).insert_time ? true : false;
    }

    /**
     * Return the picture based on the asset scope
     * @param asset The entire Asset object
     * @returns the signed url targeting the asset's picture
     */
    public getPictureUrl(asset: Asset): Observable<string> {
        let ServiceClass: AbstractType<PictureService>;
        let pictureSource: keyof ScopeSettings['pictureSources'];
        if (asset.picture) {
            ServiceClass = SCOPE_SETTINGS[asset.family.scope].pictureServices.Asset;
            pictureSource = 'asset';
        } else {
            ServiceClass = SCOPE_SETTINGS[asset.family.scope].pictureServices.Other;
            pictureSource = 'other';
        }
        const source = get(asset, SCOPE_SETTINGS[asset.family.scope].pictureSources[pictureSource]) as string;
        if(source) {
            const service = this._injector.get(ServiceClass);
            return service.getPicture(source).pipe(map((res => res.picture)));
        } else {
            return of('');
        }
    }

    /**
     * Get the default category to display on formcontrol create PF / F
     * @param context The context : family or parent family
     * @param scope The scope of the PF / F to check
     * @param categories List of categories from master data
     */
    public getDefaultCategoryFromScope(context: 'family' | 'parentFamily', scope: string, categories: Categories): Category {
        return categories[scope][context].find(category => category.id === SCOPE_SETTINGS[scope].defaultCategoryId[context]);
    }

    /**
     * Check if the category is disabled in form, depending on context, scope and isAdmin (for oversize)
     * @param context The context : family or parent family
     * @param scope The scope of the PF / F to check
     * @param isAdmin Optional boolean, used for oversize only
     */
    public isCategoryDisabled(context: 'family' | 'parentFamily', scope: string, isAdmin?: boolean): boolean {
        if (SCOPE_SETTINGS[scope].isCategoryDisabled[context]) {
            return true;
        } else if (!SCOPE_SETTINGS[scope].isCategoryDisabled[context]) {
            if (SCOPE_SETTINGS[scope].isCategoryDisabled.isAdminRoleRequired && !isAdmin) { // oversize need admin right to update
                return true;
            }
            return false;
        }
    }
}
